import { useQuery } from 'react-query';

import { APIBaseChronos } from '../hosts';
import { QueryEntity } from '../types';
import { AudioBlocks, AudioWaveform } from '../types/backend-dto-types';
import useGetFetchConfig from '../useGetFetchConfig';

// Keep these resources fresh across short-lived viewers such as reference previews,
// without retaining large transcripts and waveforms for the full signed-URL lifetime.
const AUDIO_ASSET_CACHE_TIME_MS = 10 * 60 * 1000;

export const useGetDocumentAudioUrl = (matterId: string | undefined, docId: string | undefined) => {
  const { fetchConfigGET } = useGetFetchConfig();

  return useQuery<string>(
    [QueryEntity.PresignedGetDocumentAudio, { docId }],
    async () => {
      const response = await fetch(`${APIBaseChronos}/api/matters/${matterId}/docs/${docId}/audio`, fetchConfigGET);
      if (!response.ok) throw new Error('Fetching audio URL failed');
      return response.text();
    },
    {
      enabled: !!matterId && !!docId,
      refetchOnWindowFocus: false,
      staleTime: AUDIO_ASSET_CACHE_TIME_MS,
      cacheTime: AUDIO_ASSET_CACHE_TIME_MS,
    },
  );
};

export const useGetDocumentAudioBlocks = (matterId: string | undefined, docId: string | undefined) => {
  const { fetchConfigGET } = useGetFetchConfig();

  return useQuery<AudioBlocks>(
    [QueryEntity.DocumentAudioBlocks, { docId }],
    async () => {
      const response = await fetch(
        `${APIBaseChronos}/api/matters/${matterId}/docs/${docId}/audio/blocks`,
        fetchConfigGET,
      );
      if (!response.ok) throw new Error('Fetching audio transcript failed');
      return response.json();
    },
    {
      enabled: !!matterId && !!docId,
      refetchOnWindowFocus: false,
      staleTime: AUDIO_ASSET_CACHE_TIME_MS,
      cacheTime: AUDIO_ASSET_CACHE_TIME_MS,
    },
  );
};

export const useGetDocumentAudioWaveform = (matterId: string | undefined, docId: string | undefined) => {
  const { fetchConfigGET } = useGetFetchConfig();

  return useQuery<AudioWaveform>(
    [QueryEntity.DocumentAudioWaveform, { docId }],
    async () => {
      const presignedUrlResponse = await fetch(
        `${APIBaseChronos}/api/matters/${matterId}/docs/${docId}/audio/waveform`,
        fetchConfigGET,
      );
      if (!presignedUrlResponse.ok) throw new Error('Fetching audio waveform URL failed');

      const waveformResponse = await fetch(await presignedUrlResponse.text());
      if (!waveformResponse.ok) throw new Error('Fetching audio waveform failed');

      return waveformResponse.json();
    },
    {
      enabled: !!matterId && !!docId,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: AUDIO_ASSET_CACHE_TIME_MS,
      cacheTime: AUDIO_ASSET_CACHE_TIME_MS,
    },
  );
};
